import type { ApiResponse, ReservationStatus } from '@line-crm/shared'

const API_URL = import.meta.env.VITE_API_URL
if (!API_URL) {
  throw new Error('VITE_API_URL is not set. Configure it in apps/liff/.env')
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json() as Promise<T>
}

export interface LiffMenu {
  id: string
  name: string
  category: string | null
  durationMinutes: number
  price: number
  description: string | null
  sortOrder: number
}

export interface LiffSlot {
  startAt: string
  endAt: string
  available: boolean
}

export interface LiffReservation {
  id: string
  startAt: string
  endAt: string
  status: ReservationStatus
  title: string
  totalMinutes: number
  totalPrice: number
  menus: { name: string; minutes: number; price: number }[]
  cancellable: boolean
}

/**
 * Auth payload for LIFF API calls.
 * Pass `token` (issued by webhook, carries authoritative tenant_id) when available;
 * otherwise pass `idToken` (LIFF id_token, legacy). Backend prefers `token` when both are set.
 */
export interface LiffAuth {
  token?: string | null
  idToken?: string | null
}

function authBody(auth: LiffAuth): Record<string, unknown> {
  return {
    ...(auth.token ? { token: auth.token } : {}),
    ...(auth.idToken ? { idToken: auth.idToken } : {}),
  }
}

export const liffApi = {
  menus: (auth: LiffAuth) =>
    postJson<ApiResponse<LiffMenu[]>>('/api/liff/menus', authBody(auth)),

  slots: (auth: LiffAuth, date: string, menuIds: string[]) =>
    postJson<ApiResponse<LiffSlot[]>>('/api/liff/reservations/slots', {
      ...authBody(auth), date, menuIds,
    }),

  create: (auth: LiffAuth, data: { startAt: string; menuIds: string[]; customerNote?: string }) =>
    postJson<ApiResponse<{ id: string; startAt: string; endAt: string; totalMinutes: number; totalPrice: number; status: ReservationStatus }>>(
      '/api/liff/reservations',
      { ...authBody(auth), ...data },
    ),

  myReservations: (auth: LiffAuth) =>
    postJson<ApiResponse<LiffReservation[]>>('/api/liff/reservations/my', authBody(auth)),

  cancel: (auth: LiffAuth, id: string) =>
    postJson<ApiResponse<null>>(`/api/liff/reservations/${id}/cancel`, authBody(auth)),
}
